const DEFAULT_TTL_MS = 60_000;

type CacheEntry<T> = {
  data?: T;
  expiresAt: number;
  promise?: Promise<T>;
};

const cache = new Map<string, CacheEntry<unknown>>();

export async function fetchJsonCached<T>(
  url: string,
  ttlMs = DEFAULT_TTL_MS,
): Promise<T> {
  const now = Date.now();
  const entry = cache.get(url) as CacheEntry<T> | undefined;

  if (entry?.data !== undefined && entry.expiresAt > now) {
    return entry.data;
  }

  if (entry?.promise) {
    return entry.promise;
  }

  const promise = fetch(url, { credentials: "include" }).then(async (res) => {
    if (!res.ok) throw new Error(`Request failed: ${res.status}`);
    const data = (await res.json()) as T;
    cache.set(url, { data, expiresAt: Date.now() + ttlMs });
    return data;
  });

  cache.set(url, { expiresAt: now + ttlMs, promise });

  try {
    return await promise;
  } catch (error) {
    cache.delete(url);
    throw error;
  }
}

export function invalidateApiCache(match?: string | ((key: string) => boolean)) {
  if (!match) {
    cache.clear();
    return;
  }

  for (const key of cache.keys()) {
    if (typeof match === "string" ? key.startsWith(match) : match(key)) {
      cache.delete(key);
    }
  }
}
